import type { TFunction } from "i18next";

export const PROC_SHOPIFY_FR =
  "https://choose.notion.site/Proc-dure-d-synchro-Shopify-FR-48b150f463134b2f9c9244b06d4c914d";
export const PROC_SHOPIFY_EN =
  "https://choose.notion.site/Shopify-desynchronization-ENG-45449d5553974de8a34fdd4b7456d41e";

export const getProcShopifyUrl = (language: string) => {
  return language === "fr" ? PROC_SHOPIFY_FR : PROC_SHOPIFY_EN;
};

export const getShopifyAdminUrl = (shopifyUrl: string) => {
  const shopifyId = shopifyUrl
    .replace("http://", "")
    .replace("https://", "")
    .replace(".myshopify.com", "");
  return `https://admin.shopify.com/store/${shopifyId}/products`;
};

export enum ShopifyErrorCode {
  OutOfStock,
  NotEnoughStock,
  ProductNotFound,
  Unhandled,
}

export const getProductsNotEnoughStock = (exportedErrorCodes: string[]) => {
  const refs: Record<string, number> = {};
  exportedErrorCodes
    .filter((error) => error.includes("Not enought product quantity"))
    .forEach((exportedErrorCode) => {
      const errors = exportedErrorCode
        .split("Not enought product quantity")
        .map((error) => error.trim())
        .filter((error) => error !== "");
      errors.forEach((error) => {
        error
          .split(" - ")
          .slice(1)
          .forEach((ref) => {
            let r = ref.replace("(", "").replace(")", "");
            if (r.includes(",")) {
              r = r.split(",")[0] ?? "";
            }
            if (r) {
              refs[r] = (refs[r] ?? 0) + 1;
            }
          });
      });
    });
  return refs;
};

export const getProductsWithNoStock = (exportedErrorCodes: string[]) => {
  const refs: Record<string, number> = {};
  exportedErrorCodes
    .filter((error) => error.includes("product quantity is 0"))
    .forEach((exportedErrorCode) => {
      const errors = exportedErrorCode
        .split("product quantity is 0")
        .map((error) => error.trim())
        .filter((error) => error !== "");
      errors.forEach((error) => {
        error
          .split(" - ")
          .slice(1)
          .forEach((ref) => {
            let r = ref.replace("(", "").replace(")", "");
            if (r.includes(",")) {
              r = r.split(",")[0] ?? "";
            }
            if (r) {
              refs[r] = (refs[r] ?? 0) + 1;
            }
          });
      });
    });
  return refs;
};

export const getProductsNotFound = (exportedErrorCodes: string[]) => {
  const refs: Record<string, number> = {};
  exportedErrorCodes
    .filter((error) => error.includes("Product(s) not found"))
    .forEach((exportedErrorCode) => {
      const errors = exportedErrorCode

        .split("Product(s) not found")
        .map((error) => error.trim())
        .filter((error) => error !== "");
      errors.forEach((error) => {
        error.split(",").forEach((ref) => {
          const r = ref.trim();
          if (r) {
            refs[r] = (refs[r] ?? 0) + 1;
          }
        });
      });
    });
  return refs;
};

export const getShopifyErrors = (exportedErrorCodes: string[]) => {
  const unhandledErrors = exportedErrorCodes
    .map((e) => getShopifyError(e))
    .filter((e) => e === ShopifyErrorCode.Unhandled);

  const errors: Record<ShopifyErrorCode, string> = {
    [ShopifyErrorCode.OutOfStock]: Object.entries(
      getProductsWithNoStock(exportedErrorCodes),
    )
      .map(([key, value]) => `${key} (${value})`)
      .join("\n"),
    [ShopifyErrorCode.NotEnoughStock]: Object.entries(
      getProductsNotEnoughStock(exportedErrorCodes),
    )
      .map(([key, value]) => `${key} (${value})`)
      .join("\n"),
    [ShopifyErrorCode.ProductNotFound]: Object.entries(
      getProductsNotFound(exportedErrorCodes),
    )
      .map(([key, value]) => `${key} (${value})`)
      .join("\n"),
    [ShopifyErrorCode.Unhandled]:
      unhandledErrors.length > 0 ? unhandledErrors.length.toString() : "",
  };
  return errors;
};

export const getShopifyError = (exportedErrorCode: string) => {
  if (exportedErrorCode.includes("Product(s) not found")) {
    return ShopifyErrorCode.ProductNotFound;
  }
  if (exportedErrorCode.includes("product quantity is 0")) {
    return ShopifyErrorCode.OutOfStock;
  }
  if (exportedErrorCode.includes("Not enought product quantity")) {
    return ShopifyErrorCode.NotEnoughStock;
  }
  return ShopifyErrorCode.Unhandled;
};

export const getShopifyLabelFromError = (
  shopifyError: ShopifyErrorCode,
  t: TFunction,
  products?: string,
) => {
  switch (shopifyError) {
    case ShopifyErrorCode.ProductNotFound:
      return t("orders.shopify_errors.product_not_found", {
        products,
      });
    case ShopifyErrorCode.OutOfStock:
      return t("orders.shopify_errors.out_of_stock", {
        products,
      });
    case ShopifyErrorCode.NotEnoughStock:
      return t("orders.shopify_errors.not_enough_stock", {
        products,
      });
    default:
      return t("orders.shopify_errors.unhandled", {
        products,
      });
  }
};
